import _ from 'lodash';
import { createActionThunk } from 'redux-thunk-actions';

import {
  LOGIN,
  LOGOUT,
  REGISTER,
  REGISTER_CHECK,
  RESET_LOGIN,
  CLEAR_TOKEN,
  RESET_PASSWORD,
  FORGOT_PASSWORD,
  SET_LOGIN_MESSAGE,
  SET_LOGIN_REDIRECT,
  VERIFY_PHONE_NUMBER,
  SEND_VERIFICATION_CODE,
  UPDATE_PROFILE,
  LOGIN_AS_USER,
  SET_SUPERADMIN,
  CHECK_TOKEN_VALID
} from '../types/authTypes';
import AuthService from '../../../services/auth';

const removeTokens = () => {
  window.localStorage.removeItem('authToken');
  window.sessionStorage.removeItem('authToken');
  window.localStorage.removeItem('authUserData');
  window.sessionStorage.removeItem('authUserData');
  window.sessionStorage.removeItem('authTokenAs');
  window.sessionStorage.removeItem('authUserDataAs');
  window.sessionStorage.removeItem('authTokenAsOrigin');
};

export const login = createActionThunk(LOGIN, async (login, password, remember) => {
  try {
    const { token, userData } = await AuthService.login(login, password, remember);

    removeTokens();

    if (remember) {
      localStorage.setItem('authToken', token);
      localStorage.setItem('authUserData', JSON.stringify(userData));
    } else {
      sessionStorage.setItem('authToken', token);
      sessionStorage.setItem('authUserData', JSON.stringify(userData));
    }

    return { token, userData };
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ??
        error?.message ??
        'Login failed. Please try again.',
    );
  }
});

export const sendVerificationCode = createActionThunk(SEND_VERIFICATION_CODE, (data) =>
  AuthService.sendVerificationCode(data).then((data) => data),
  false
);

export const verifyPhoneNumber = createActionThunk(VERIFY_PHONE_NUMBER, (data) =>
  AuthService.verifyPhoneNumber(data).then((data) => data),
  false
);

export const register = createActionThunk(REGISTER, (data) =>
  AuthService.register(data).then((data) => data),
  false
);

export const registerCheck = createActionThunk(REGISTER_CHECK, (data) =>
  AuthService.registerCheck(data).then((data) => data),
  false
);

export const forgotPassword = createActionThunk(FORGOT_PASSWORD, (data) =>
  AuthService.forgotPassword(data).then((data) => data),
  false
);

export const resetPassword = createActionThunk(RESET_PASSWORD, (data) =>
  AuthService.resetPassword(data).then((data) => data),
  false
);

export const checkTokenValid = createActionThunk(CHECK_TOKEN_VALID, (data) =>
  AuthService.checkTokenValid(data).then((data) => data),
  false
);

export const logout = createActionThunk(LOGOUT, () => {
  removeTokens();
  AuthService.logout();
});

export const clearToken = createActionThunk(CLEAR_TOKEN, removeTokens);

export const setLoginMessage = createActionThunk(SET_LOGIN_MESSAGE,(message) => message);

export const setLoginRedirect = createActionThunk(SET_LOGIN_REDIRECT, (url) => url);

export const resetLogin = createActionThunk(RESET_LOGIN, _.noop);

export const loginAsUser = createActionThunk(LOGIN_AS_USER, async (id) => {
  try {
    const { token, userData } = await AuthService.loginAsUser(id);

    // Capture the admin token from whichever storage holds it (remember-me
    // puts it in localStorage, otherwise sessionStorage). We must clear it
    // from both, otherwise getStoredAuthToken() reads localStorage first
    // and keeps signing API calls as the admin.
    const adminTokenLocal = localStorage.getItem('authToken');
    const adminUserDataLocal = localStorage.getItem('authUserData');
    const adminTokenSession = sessionStorage.getItem('authToken');
    const adminUserDataSession = sessionStorage.getItem('authUserData');

    const adminToken = adminTokenLocal ?? adminTokenSession;
    const adminUserData = adminUserDataLocal ?? adminUserDataSession;
    const adminOrigin = adminTokenLocal ? 'local' : 'session';

    if (adminToken) sessionStorage.setItem('authTokenAs', adminToken);
    if (adminUserData) sessionStorage.setItem('authUserDataAs', adminUserData);
    sessionStorage.setItem('authTokenAsOrigin', adminOrigin);

    localStorage.removeItem('authToken');
    localStorage.removeItem('authUserData');

    sessionStorage.setItem('authToken', token);
    sessionStorage.setItem('authUserData', JSON.stringify(userData));

    return { token, userData };
  } catch (error) {
    throw new Error(error?.response?.data?.message ?? error?.message ?? 'Login as failed');
  }
});

export const setSuperAdmin = createActionThunk(SET_SUPERADMIN, () => {
  const adminToken = sessionStorage.getItem('authTokenAs');
  const adminUserData = sessionStorage.getItem('authUserDataAs');
  const origin = sessionStorage.getItem('authTokenAsOrigin');

  // Restore the admin token to its original storage so subsequent
  // page reloads pick it up the same way the admin originally logged in.
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('authUserData');
  sessionStorage.removeItem('authTokenAs');
  sessionStorage.removeItem('authUserDataAs');
  sessionStorage.removeItem('authTokenAsOrigin');

  const target = origin === 'local' ? localStorage : sessionStorage;
  if (adminToken) target.setItem('authToken', adminToken);
  if (adminUserData) target.setItem('authUserData', adminUserData);

  return {
    token: adminToken,
    userData: adminUserData ? JSON.parse(adminUserData) : {},
  };
});

export const updateProfile = createActionThunk(UPDATE_PROFILE, async (data) => {
    const { userData } = await AuthService.updateProfile(data).then((data) => data);
  
    if(localStorage.getItem('authUserData')){
      localStorage.setItem('authUserData', JSON.stringify(userData));
    }
    if(sessionStorage.getItem('authUserData')){
      sessionStorage.setItem('authUserData', JSON.stringify(userData));
    }
    
    return { userData };
},false);
